// Ad/campaign attribution for lead tracking.
//
// The problem: UTM params only exist on the URL of the FIRST page a visitor
// lands on. They'll usually browse to /contact before submitting, by which
// point the params are gone. So we capture them on arrival and keep them for
// the session ("first-touch" attribution, the standard for lead gen).
//
// First-touch is deliberate: the ad that *earned* the visit gets the credit,
// and we never overwrite it with a later internal navigation.

const STORAGE_KEY = 'adroit_attribution';

const UTM_KEYS = {
    utm_source: 'source',
    utm_medium: 'medium',
    utm_campaign: 'name',
    utm_term: 'term',
    utm_content: 'content',
    gclid: 'gclid',
    fbclid: 'fbclid',
};

function safeSession() {
    // Private browsing / blocked storage must never break the site.
    try {
        const s = window.sessionStorage;
        s.setItem('__t', '1');
        s.removeItem('__t');
        return s;
    } catch (e) {
        return null;
    }
}

/**
 * Read campaign params off the current URL and store them if this is the
 * visitor's first page of the session. Safe to call on every load.
 */
export function captureAttribution() {
    if (typeof window === 'undefined') return;
    const store = safeSession();

    // Already captured this session — keep first-touch, don't overwrite.
    if (store && store.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const data = {};
    for (const [param, field] of Object.entries(UTM_KEYS)) {
        const value = params.get(param);
        if (value) data[field] = value.slice(0, 200);
    }

    // Record where they landed and who sent them, even without UTMs — useful
    // for organic/referral leads too.
    data.landingPage = `${window.location.pathname}${window.location.search}`.slice(0, 500);
    if (document.referrer) data.referrer = document.referrer.slice(0, 500);

    if (store) {
        try {
            store.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            /* storage full / blocked — attribution is best-effort */
        }
    }
}

/**
 * The stored campaign data, shaped for the API's `campaign` field.
 * Returns undefined when there's nothing worth sending.
 */
export function getAttribution() {
    if (typeof window === 'undefined') return undefined;
    const store = safeSession();
    if (!store) return undefined;
    try {
        const raw = store.getItem(STORAGE_KEY);
        if (!raw) return undefined;
        const data = JSON.parse(raw);
        // `referrer` isn't part of the API schema (the server records its own).
        const { referrer: _referrer, ...campaign } = data;
        return Object.keys(campaign).length ? campaign : undefined;
    } catch (e) {
        return undefined;
    }
}
